import i18n from '@/shared/i18n';
import {
  DATE_FORMAT_TOKENS,
  formatDate,
  formatEpochMs,
  parseTimestamp,
} from '@/shared/utils/datetime-utils';

import { BrowserLiveView } from '@/modules/outputs/components/BrowserLiveView';

import { RainTestRunCard } from '../../components/RainTestRunCard';
import { RainRunCodeCard } from '../cards/RainRunCodeCard';
import type { IChatMessage } from '@kognitos/lattice';

import type {
  IRainToolCallState,
  IRainToolRenderContext,
  TRainToolRenderer,
} from '../../hooks/types';
import type { IQuillTestRunFocus } from '../quill';
import { buildAutomationSavedMessage } from '../../utils/message-helpers';
import { TOOL_CALL_ID_PREFIX } from '../../constants';
import {
  extractRunCreateTime,
  extractSavedAutomationVersion,
  extractTestAutomationRunId,
  parseRunCodeResult,
  readRunCodePurpose,
} from '../quill-run-result';

/**
 * A committed `save_automation` surfaces a "Draft automation updated to v…"
 * status line (KOG-11802), reusing the generic card's id so it replaces the
 * in-progress card in place. Falls back to the generic card while pending or
 * errored, or when the result carries no version.
 */
export function renderAutomationSavedCard(
  state: IRainToolCallState,
  ctx: IRainToolRenderContext,
): IChatMessage {
  const version = extractSavedAutomationVersion(state.result);
  if (state.status === 'success' && version) {
    return buildAutomationSavedMessage(
      `${TOOL_CALL_ID_PREFIX}${state.id}`,
      version,
    );
  }
  return ctx.renderGenericCard(state);
}

/**
 * `test_automation` / `replay_run` — quill2's build-loop test tools. Each gets a
 * "view run" row that focuses the step view on that run.
 */
export function createTestRunCardRenderer(
  focus: IQuillTestRunFocus,
): TRainToolRenderer {
  return (state, ctx) => {
    const runId = extractTestAutomationRunId(state.result);
    if (!runId) return ctx.renderGenericCard(state);
    // Prefer the run's own `create_time` (jeeves' authoritative, immutable
    // run-creation clock, already present in this same result) over
    // `startedAtMs` (rain's event-log clock, from the `ToolCallStart` event) —
    // see `extractRunCreateTime`. Only falls back to `startedAtMs` if a
    // particular run's result is somehow missing it.
    const createTime = extractRunCreateTime(state.result);
    const timeText = createTime
      ? formatDate(
          parseTimestamp(createTime),
          DATE_FORMAT_TOKENS.SHORT_DATE_TIME,
        )
      : state.startedAtMs
        ? formatEpochMs(state.startedAtMs, DATE_FORMAT_TOKENS.SHORT_DATE_TIME)
        : undefined;
    const label = timeText
      ? i18n.t(
          state.status === 'running'
            ? 'automation.quillChat.toolCall.viewRunPending'
            : 'automation.quillChat.toolCall.viewRun',
          { ns: 'automation', time: timeText },
        )
      : i18n.t('automation.quillChat.toolCall.viewRunNoTime', {
          ns: 'automation',
        });
    const isViewingRun = focus.focusedToolCallId === state.id;
    return ctx.renderCustomCard(
      state,
      <RainTestRunCard
        label={label}
        status={state.status}
        isViewingRun={isViewingRun}
        onClick={() =>
          focus.onFocus?.(
            isViewingRun
              ? undefined
              : {
                  toolCallId: state.id,
                  runId,
                  startedAtMs: state.startedAtMs,
                },
          )
        }
      />,
    );
  };
}

/**
 * `run_code` — quill2's sandbox probe, labelled by its `purpose` argument. The
 * generic card is not an option here: it prints the arguments verbatim, and
 * these are SPy source, connection ids and a config map.
 */
export function renderRunCodeCard(
  state: IRainToolCallState,
  ctx: IRainToolRenderContext,
): IChatMessage {
  const { executionId, executionStatus, hasSetOutputs } = parseRunCodeResult(
    state.result,
  );
  return ctx.renderCustomCard(
    state,
    <RainRunCodeCard
      purpose={readRunCodePurpose(state.args)}
      status={state.status}
      // Withheld until a result exists to have read it from.
      executionStatus={state.result === undefined ? undefined : executionStatus}
      // `hasSetOutputs` implies COMPLETED: no other status carries an outputs map.
      outputsExecutionId={hasSetOutputs ? executionId : undefined}
    />,
  );
}

/**
 * Reads the live view URL off a `show_browser_session` call. The agent is the
 * only source for it — the URL comes back from the browser book's "provision a
 * browser" and is passed straight through — so an absent or non-string value
 * means the agent called the tool wrong, and the card renders nothing rather
 * than an empty player.
 */
export function readBrowserSessionUrl(
  args: Record<string, unknown>,
): string | undefined {
  const value = args.live_view_url;
  if (typeof value !== 'string') return undefined;
  const url = value.trim();
  return url || undefined;
}

/** Reads the optional session id used as the card's title. */
export function readBrowserSessionId(
  args: Record<string, unknown>,
): string | undefined {
  const value = args.session_id;
  if (typeof value !== 'string') return undefined;
  const id = value.trim();
  return id || undefined;
}

/**
 * `show_browser_session` renders the session's live screencast inline, so the
 * user can watch the browser while the agent drives it.
 *
 * The card is built from the call's *arguments*, not its result: the tool is
 * auto-acknowledged as soon as its arguments finish streaming (see
 * `quill.config.ts`), so waiting for a result would only delay the player.
 *
 * It keeps rendering after the call resolves — the screencast endpoint serves
 * the recorded video once the session ends, which is what the Run Log shows too.
 */
export function renderBrowserSessionCard(
  state: IRainToolCallState,
  ctx: IRainToolRenderContext,
): IChatMessage {
  const liveViewUrl = readBrowserSessionUrl(state.args);
  if (!liveViewUrl) {
    return ctx.renderCustomCard(state, null);
  }
  return ctx.renderCustomCard(
    state,
    <BrowserLiveView
      liveViewUrl={liveViewUrl}
      name={readBrowserSessionId(state.args)}
      panelSessionId={state.id}
      panelSessionStartedAtMs={state.startedAtMs}
      // A new session takes focus in the rail; older cards can reopen theirs.
      sidePanel
      autoOpenSidePanel
    />,
  );
}
